import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import * as BikeApi from "features/bike/api/BikeApi"
import * as DocumentApi from "services/api/DocumentApi"

import PrivateLayout from "components/Layout/PrivateLayout"
import { privateRoutes } from "router/routes"

import { checkIsPrivateConsumer, isNotEmpty, pageStatusEnum } from "utils"
import { useAlert, useOperator, useUser } from "hooks"

import { BackIcon, Button, Spinner } from "components/Elements"
import {
	BikeCoOwner,
	BikeDescription,
	BikeInfo,
	BikePhoto,
	BikeDocuments,
	BikeSerials,
	BikeStatus,
	BikeName,
	BikePrice,
	BikeCertificate,
} from "../components"
import { ErrorResponse } from "models"
import { DocumentsReducer, BikeReducer } from "../hooks/bikePageReducer"
import { Seo } from "components/Seo"

export const BikeDetailsPage: React.FC = () => {
	const [{ bikeStatus, bike }, dispatchBike] = React.useReducer(BikeReducer, {
		bikeStatus: pageStatusEnum.loading,
		bike: null,
	})
	const [{ documentsStatus, documents }, dispatchDocuments] = React.useReducer(DocumentsReducer, {
		documentsStatus: pageStatusEnum.loading,
		documents: [],
	})

	const { t } = useTranslation()
	const { alert } = useAlert()
	const { user } = useUser()
	const { currentOperator } = useOperator()

	const navigate = useNavigate()
	const { slug: bikeId } = useParams<{ slug: string }>()

	const isBikeLoading = bikeStatus === pageStatusEnum.loading
	const isBikeComplete = bikeStatus === pageStatusEnum.complete
	const isBikeErrored = bikeStatus === pageStatusEnum.errored

	const isDocumentsComplete = documentsStatus === pageStatusEnum.complete
	const isDocumentsErrored = documentsStatus === pageStatusEnum.errored

	const fetchBike = async () => {
		try {
			dispatchBike({ type: "LOADING" })
			const { res: fetchedBike, getError: getBikeError } = await BikeApi.getBike(bikeId)
			if (getBikeError() || !fetchedBike) throw getBikeError()
			dispatchBike({ type: "SUCCESS", bike: fetchedBike })
			isNotEmpty(currentOperator)
			// We fetch documents if we have the bike
			await fetchDocuments()
		} catch (error) {
			console.error(error)
			dispatchBike({ type: "ERROR" })
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)

			// If bike not found => redirect to dashboard
			if ((error as ErrorResponse)?.code === "RessourceNotFoundException") {
				navigate(privateRoutes.dashboard.path)
			}
		}
	}

	const fetchDocuments = async () => {
		try {
			dispatchDocuments({ type: "LOADING" })
			const { res: fetchedDocuments, getError } = await DocumentApi.getDocuments(bikeId)
			if (getError() || !fetchedDocuments) throw getError()
			dispatchDocuments({ type: "SUCCESS", documents: fetchedDocuments })
		} catch (error) {
			console.error(error)
			dispatchDocuments({ type: "ERROR" })
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
		}
	}

	React.useEffect(() => {
		fetchBike()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const isPrivateUser = checkIsPrivateConsumer({
		gender: user.description.gender,
	})

	return (
		<PrivateLayout>
			{isBikeComplete && (
				<>
					<Seo
						title={t("bike", {
							ns: "seo",
							operator: currentOperator.name,
						})}
					/>
					<div className="max-w-screen-3xl mx-auto px-4 py-8 lg:py-16 lg:pt-8 relative">
						<button
							aria-label={t("description.back_to_list", {
								ns: "bike",
							})}
							type="button"
							onClick={() => navigate(-1)}
						>
							<BackIcon size="60" color="var(--primaryColor)" />
						</button>

						<div className="flex flex-wrap">
							<aside className="xl:w-3/12 w-full xl:pr-6">
								<div className="c-block px-4 py-6 mt-8 mb-12">
									<BikePhoto
										bike={bike}
										documents={documents}
										refreshDocuments={fetchDocuments}
									/>
									<BikeName bike={bike} refreshBike={fetchBike} />
								</div>

								<BikeCertificate bike={bike} currentOperator={currentOperator} />

								<div className="c-block px-4 py-6 mt-8 mb-12">
									{isDocumentsErrored && (
										<div className="flex flex-col justify-center my-8">
											{t("file_default_error", { ns: "form" })}
											<Button
												variant="primaryBordered"
												size="small"
												type="button"
												onClick={fetchDocuments}
												className="mx-1 mt-2 px-3 py-1"
											>
												{t("global.retry")}
											</Button>
										</div>
									)}
									{isDocumentsComplete && (
										<BikeDocuments
											bike={bike}
											documents={documents}
											refreshDocuments={fetchDocuments}
										/>
									)}

									<BikePrice bike={bike} refreshBike={fetchBike} />
								</div>
							</aside>

							<section className="xl:w-9/12 w-full xl:pl-6">
								<BikeDescription
									bike={bike}
									refreshBike={fetchBike}
								></BikeDescription>

								<BikeStatus bike={bike} refreshBike={fetchBike} />

								<div className="flex flex-wrap justify-between">
									<div
										className={`${
											isPrivateUser
												? "xl:w-6/12 w-full xl:mr-6 mr-0"
												: "flex-1"
										}`}
									>
										<BikeSerials bike={bike} refreshBike={fetchBike} />
									</div>

									{isPrivateUser && (
										<div className="flex-1 xl:ml-6 ml-0">
											<BikeCoOwner bike={bike} refreshBike={fetchBike} />
										</div>
									)}
								</div>

								<BikeInfo bike={bike} refreshBike={fetchBike} />
							</section>
						</div>

						{isBikeErrored && (
							<div className="flex justify-center my-8">
								{t("account.error", { ns: "auth" })}
							</div>
						)}

						{isBikeLoading && (
							<div className="flex justify-center my-8">
								<Spinner color={"var(--primaryColor)"} size={"100px"} />
							</div>
						)}
					</div>
				</>
			)}
		</PrivateLayout>
	)
}
